/**
 * Affected Range Calculator
 *
 * Maps (toolName, args) → list of cell ranges that will be modified.
 * Used by the delta capture system to know which cells to snapshot
 * before a tool executes.
 */

import { getCsvAttachment } from '../agents/csv-store'
import { parseAddressWithSheet } from '../excel-utils'

// ============================================================================
// Types
// ============================================================================

export type CaptureStrategy =
  | 'targeted'
  | 'sheet-scan'
  | 'full-snapshot'
  | 'none'

export interface AffectedRange {
  sheetName: string | null // null = active sheet
  address: string // Range address without sheet prefix (e.g., "A1:C10")
}

export interface AffectedRangeResult {
  strategy: CaptureStrategy
  ranges: AffectedRange[]
  /** For structural changes, capture the entire sheet */
  fullSheets: string[]
  /** Sheet names to mark dirty */
  dirtySheets: (string | null)[]
}

// ============================================================================
// Address Parsing Helpers
// ============================================================================

function parseAddress(address: string): AffectedRange {
  const { sheetName, rangeAddress } = parseAddressWithSheet(address)
  return { sheetName, address: rangeAddress }
}

// ============================================================================
// Per-Tool Range Calculators
// ============================================================================

type ToolArgs = Record<string, unknown>

function getWriteCellRangesAffected(args: ToolArgs): AffectedRangeResult {
  const items = args.items as Array<{ address: string; values?: unknown[][] }>
  const ranges = items.map((item) => parseAddress(item.address))
  const dirtySheets = items.map(
    (item) => parseAddressWithSheet(item.address).sheetName
  )
  return { strategy: 'targeted', ranges, fullSheets: [], dirtySheets }
}

function getSetFormulaRangesAffected(args: ToolArgs): AffectedRangeResult {
  const items = args.items as Array<{ address: string }>
  const ranges = items.map((item) => parseAddress(item.address))
  const dirtySheets = items.map(
    (item) => parseAddressWithSheet(item.address).sheetName
  )
  return { strategy: 'targeted', ranges, fullSheets: [], dirtySheets }
}

function getClearCellRangeAffected(args: ToolArgs): AffectedRangeResult {
  const items = args.items as Array<{ address: string }>
  const ranges = items.map((item) => parseAddress(item.address))
  const dirtySheets = items.map(
    (item) => parseAddressWithSheet(item.address).sheetName
  )
  return { strategy: 'targeted', ranges, fullSheets: [], dirtySheets }
}

function getFormatCellsAffected(args: ToolArgs): AffectedRangeResult {
  const items = args.items as Array<{ address: string }>
  const ranges = items.map((item) => parseAddress(item.address))
  const dirtySheets = items.map(
    (item) => parseAddressWithSheet(item.address).sheetName
  )
  // Format changes don't affect values/formulas, but we still track for dirty
  return { strategy: 'targeted', ranges, fullSheets: [], dirtySheets }
}

function getSetBordersAffected(args: ToolArgs): AffectedRangeResult {
  const addresses = args.addresses as string[] | undefined
  if (!Array.isArray(addresses) || addresses.length === 0) {
    return {
      strategy: 'sheet-scan',
      ranges: [],
      fullSheets: [],
      dirtySheets: [null],
    }
  }
  const ranges = addresses.map((addr) => parseAddress(addr))
  const dirtySheets = addresses.map(
    (addr) => parseAddressWithSheet(addr).sheetName
  )
  return { strategy: 'targeted', ranges, fullSheets: [], dirtySheets }
}

function getCopyRangeAffected(args: ToolArgs): AffectedRangeResult {
  const targetAddress = args.targetAddress as string
  const sourceAddress = args.sourceAddress as string
  // The target range will be overwritten; we also need source dims to estimate
  const targetParsed = parseAddress(targetAddress)
  const sourceParsed = parseAddress(sourceAddress)
  return {
    strategy: 'targeted',
    // We capture target range (which gets overwritten).
    // The source range dimensions define the target extent, but we
    // only need the target start cell — the actual capture reads
    // the range that will be overwritten.
    ranges: [targetParsed, sourceParsed],
    fullSheets: [],
    dirtySheets: [parseAddressWithSheet(targetAddress).sheetName],
  }
}

function getMoveRangeAffected(args: ToolArgs): AffectedRangeResult {
  const sourceAddress = args.sourceAddress as string
  const targetAddress = args.targetAddress as string
  return {
    strategy: 'targeted',
    ranges: [parseAddress(sourceAddress), parseAddress(targetAddress)],
    fullSheets: [],
    dirtySheets: [
      parseAddressWithSheet(sourceAddress).sheetName,
      parseAddressWithSheet(targetAddress).sheetName,
    ],
  }
}

function getSortRangeAffected(args: ToolArgs): AffectedRangeResult {
  const address = args.address as string
  const parsed = parseAddress(address)
  return {
    strategy: 'targeted',
    ranges: [parsed],
    fullSheets: [],
    dirtySheets: [parseAddressWithSheet(address).sheetName],
  }
}

function getMergeCellsAffected(args: ToolArgs): AffectedRangeResult {
  const items = args.items as Array<{ address: string }>
  const ranges = items.map((item) => parseAddress(item.address))
  const dirtySheets = items.map(
    (item) => parseAddressWithSheet(item.address).sheetName
  )
  return { strategy: 'targeted', ranges, fullSheets: [], dirtySheets }
}

function getUnmergeCellsAffected(args: ToolArgs): AffectedRangeResult {
  const items = args.items as Array<{ address: string }>
  const ranges = items.map((item) => parseAddress(item.address))
  const dirtySheets = items.map(
    (item) => parseAddressWithSheet(item.address).sheetName
  )
  return { strategy: 'targeted', ranges, fullSheets: [], dirtySheets }
}

function getPasteAttachedCsvAffected(args: ToolArgs): AffectedRangeResult {
  const startCell = (args.startCell as string) || 'A1'
  const fileIndex = (args.fileIndex as number) ?? 0
  const csv = getCsvAttachment(fileIndex)

  if (!csv) {
    // Can't determine range without the CSV data
    return {
      strategy: 'sheet-scan',
      ranges: [],
      fullSheets: [],
      dirtySheets: [null],
    }
  }

  // The CSV data defines the extent of the write
  const parsed = parseAddress(startCell)
  return {
    strategy: 'targeted',
    ranges: [parsed],
    fullSheets: [],
    dirtySheets: [parseAddressWithSheet(startCell).sheetName],
  }
}

function getFindAndReplaceAffected(args: ToolArgs): AffectedRangeResult {
  const address = args.address as string | undefined
  if (address) {
    const parsed = parseAddress(address)
    return {
      strategy: 'targeted',
      ranges: [parsed],
      fullSheets: [],
      dirtySheets: [parseAddressWithSheet(address).sheetName],
    }
  }
  // No address = entire active sheet
  return {
    strategy: 'sheet-scan',
    ranges: [],
    fullSheets: [],
    dirtySheets: [null],
  }
}

function getAutoFillAffected(args: ToolArgs): AffectedRangeResult {
  const destinationAddress = args.destinationAddress as string
  const parsed = parseAddress(destinationAddress)
  return {
    strategy: 'targeted',
    ranges: [parsed],
    fullSheets: [],
    dirtySheets: [parseAddressWithSheet(destinationAddress).sheetName],
  }
}

function getRemoveDuplicatesAffected(args: ToolArgs): AffectedRangeResult {
  const address = args.address as string
  const parsed = parseAddress(address)
  return {
    strategy: 'targeted',
    ranges: [parsed],
    fullSheets: [],
    dirtySheets: [parseAddressWithSheet(address).sheetName],
  }
}

function getAddHyperlinkAffected(args: ToolArgs): AffectedRangeResult {
  const items = args.items as Array<{ address: string }>
  const ranges = items.map((item) => parseAddress(item.address))
  const dirtySheets = items.map(
    (item) => parseAddressWithSheet(item.address).sheetName
  )
  return { strategy: 'targeted', ranges, fullSheets: [], dirtySheets }
}

// ============================================================================
// Structural Tools (full sheet capture needed)
// ============================================================================

function getInsertRowsAffected(_args: ToolArgs): AffectedRangeResult {
  // Inserting rows shifts everything — capture active sheet
  return {
    strategy: 'sheet-scan',
    ranges: [],
    fullSheets: [],
    dirtySheets: [null],
  }
}

function getInsertColumnsAffected(_args: ToolArgs): AffectedRangeResult {
  return {
    strategy: 'sheet-scan',
    ranges: [],
    fullSheets: [],
    dirtySheets: [null],
  }
}

function getDeleteRowsAffected(_args: ToolArgs): AffectedRangeResult {
  return {
    strategy: 'sheet-scan',
    ranges: [],
    fullSheets: [],
    dirtySheets: [null],
  }
}

function getDeleteColumnsAffected(_args: ToolArgs): AffectedRangeResult {
  return {
    strategy: 'sheet-scan',
    ranges: [],
    fullSheets: [],
    dirtySheets: [null],
  }
}

function getDeleteWorksheetAffected(args: ToolArgs): AffectedRangeResult {
  const sheetName = args.sheetName as string
  return {
    strategy: 'full-snapshot',
    ranges: [],
    fullSheets: [sheetName],
    dirtySheets: [sheetName],
  }
}

// ============================================================================
// No-op Tools (metadata only, no cell changes)
// ============================================================================

function getNoopAffected(): AffectedRangeResult {
  return { strategy: 'none', ranges: [], fullSheets: [], dirtySheets: [] }
}

// ============================================================================
// executeCode — unpredictable, active sheet fallback
// ============================================================================

function getExecuteCodeAffected(_args: ToolArgs): AffectedRangeResult {
  return {
    strategy: 'sheet-scan',
    ranges: [],
    fullSheets: [],
    dirtySheets: [null], // null = active sheet, but truly unknown
  }
}

// ============================================================================
// Main Calculator
// ============================================================================

const toolHandlers: Record<string, (args: ToolArgs) => AffectedRangeResult> = {
  // Targeted captures
  writeCellRanges: getWriteCellRangesAffected,
  setFormulaRanges: getSetFormulaRangesAffected,
  clearCellRange: getClearCellRangeAffected,
  formatCells: getFormatCellsAffected,
  copyRange: getCopyRangeAffected,
  moveRange: getMoveRangeAffected,
  sortRange: getSortRangeAffected,
  mergeCells: getMergeCellsAffected,
  unmergeCells: getUnmergeCellsAffected,
  pasteAttachedCsvToSheet: getPasteAttachedCsvAffected,
  findAndReplace: getFindAndReplaceAffected,
  autoFill: getAutoFillAffected,
  removeDuplicates: getRemoveDuplicatesAffected,
  addHyperlink: getAddHyperlinkAffected,

  // Structural (sheet-scan)
  insertRows: getInsertRowsAffected,
  insertColumns: getInsertColumnsAffected,
  deleteRows: getDeleteRowsAffected,
  deleteColumns: getDeleteColumnsAffected,
  deleteWorksheet: getDeleteWorksheetAffected,

  // No-op (no cell changes)
  createWorksheet: getNoopAffected,
  renameWorksheet: getNoopAffected,
  listWorksheets: getNoopAffected,
  switchWorksheet: getNoopAffected,
  readCellRanges: getNoopAffected,
  readSheetContext: getNoopAffected,
  getCellFormat: getNoopAffected,
  getSheetDimensions: getNoopAffected,
  getSelectedRange: getNoopAffected,
  selectRange: getNoopAffected,
  createChart: getNoopAffected,
  deleteChart: getNoopAffected,
  updateChartData: getNoopAffected,
  setChartPosition: getNoopAffected,
  listCharts: getNoopAffected,
  createTable: getNoopAffected,
  getTableRows: getNoopAffected,
  addTableRow: getNoopAffected,
  addTableColumn: getNoopAffected,
  deleteTableColumn: getNoopAffected,
  resizeTable: getNoopAffected,
  convertTableToRange: getNoopAffected,
  clearTableFilters: getNoopAffected,
  reapplyTableFilters: getNoopAffected,
  listTableColumns: getNoopAffected,
  deleteTable: getNoopAffected,
  freezePanes: getNoopAffected,
  getComments: getNoopAffected,
  addComment: getNoopAffected,
  updateComment: getNoopAffected,
  deleteComment: getNoopAffected,
  saveWorkbook: getNoopAffected,
  getActiveCell: getNoopAffected,
  getUsedRange: getNoopAffected,
  getRangeSize: getNoopAffected,
  getSpecialCells: getNoopAffected,
  copyWorksheet: getNoopAffected,
  hideWorksheet: getNoopAffected,
  showWorksheet: getNoopAffected,
  groupRange: getNoopAffected,
  ungroupRange: getNoopAffected,
  setColumnWidth: getNoopAffected,
  setRowHeight: getNoopAffected,
  autofitColumns: getNoopAffected,
  autofitRows: getNoopAffected,
  fetchDataFromUrl: getNoopAffected,
  queryDatabase: getNoopAffected,
  queryDatabaseToSheet: getNoopAffected,

  // Unpredictable
  executeCode: getExecuteCodeAffected,
  executeCodeExcel: getExecuteCodeAffected,
  executeCodeInAppsScript: getExecuteCodeAffected,

  // Border/format (no value changes, but mark dirty)
  setBorders: getSetBordersAffected,

  // Cross-platform metadata tools (no-op for delta capture)
  createNamedRange: getNoopAffected,
  getNamedRanges: getNoopAffected,
  deleteNamedRange: getNoopAffected,
  setFilter: getNoopAffected,
  removeFilter: getNoopAffected,
  addNote: getNoopAffected,
  getNotes: getNoopAffected,
  setDataValidation: getNoopAffected,
  clearDataValidation: getNoopAffected,
  getDataValidation: getNoopAffected,
  addConditionalFormatRule: getNoopAffected,
  clearConditionalFormatRules: getNoopAffected,
  getConditionalFormatRules: getNoopAffected,
  setCellBorders: getFormatCellsAffected,
  clearCellBorders: getFormatCellsAffected,
  protectSheet: getNoopAffected,
  unprotectSheet: getNoopAffected,
}

/**
 * Get the affected ranges for a tool call.
 * Returns the capture strategy and list of ranges to snapshot.
 */
export function getAffectedRanges(
  toolName: string,
  args: ToolArgs
): AffectedRangeResult {
  const handler = toolHandlers[toolName]
  if (handler) {
    return handler(args)
  }
  // Unknown tool — fall back to sheet-scan for safety
  console.warn(
    `[AffectedRange] Unknown tool "${toolName}", using sheet-scan fallback`
  )
  return {
    strategy: 'sheet-scan',
    ranges: [],
    fullSheets: [],
    dirtySheets: [null],
  }
}

/**
 * Check if a tool is a mutating tool (changes cell values).
 * Read-only tools point to the getNoopAffected handler.
 */
export function isMutatingTool(toolName: string): boolean {
  const handler = toolHandlers[toolName]
  if (!handler) return true // unknown tools assumed mutating
  return handler !== getNoopAffected
}
